import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import SpinnerLoad from 'views/finance/moduleComponents/SpinnerLoad';
import ModalConfirm from 'views/finance/moduleComponents/ModalConfirm';
import {
  createFlowPreliminaryTransaction,
  updateFlowPreliminaryTransaction,
  deleteFlowPreliminaryTransaction,
} from 'views/finance/moduleServices/flow_requests';
import { formatDateLocal } from 'shared_items/formatConventions';

const emptyForm = () => ({
  date: formatDateLocal(new Date()),
  hour: '00:00:00',
  desc: '',
  categ_l0: '',
  categ_l1: '',
  categ_l2: '',
  reference: '',
  flow_type: 'Out',
  flow_value: '',
  flow_currency: 'BRL',
  std_value: '',
  flow_account: '',
  extras_description: '',
  extras_category: '',
  extras_id: '',
  extras_cardnumber: '',
  extras_info_json: '',
  exchange_id: '',
  exchange_rate: '',
  status: '',
  source: '',
  external_id: '',
});

const formatHour = (h) => {
  if (!h) return '00:00:00';
  if (typeof h === 'string') return h.length >= 8 ? h.slice(0, 8) : h;
  return '00:00:00';
};

const rowToForm = (row) => ({
  date: row.date,
  hour: formatHour(row.hour),
  desc: row.desc || '',
  categ_l0: row.categ_l0 || '',
  categ_l1: row.categ_l1 || '',
  categ_l2: row.categ_l2 || '',
  reference: row.reference || '',
  flow_type: row.flow_type || 'Out',
  flow_value: row.flow_value != null ? String(row.flow_value) : '',
  flow_currency: row.flow_currency || 'BRL',
  std_value: row.std_value != null ? String(row.std_value) : '',
  flow_account: row.flow_account || '',
  extras_description: row.extras_description || '',
  extras_category: row.extras_category || '',
  extras_id: row.extras_id || '',
  extras_cardnumber: row.extras_cardnumber || '',
  extras_info_json: row.extras_info ? JSON.stringify(row.extras_info, null, 2) : '',
  exchange_id: row.exchange_id || '',
  exchange_rate: row.exchange_rate != null ? String(row.exchange_rate) : '',
  status: row.status || '',
  source: row.source || '',
  external_id: row.external_id || '',
});

const formToPayload = (form) => {
  let extras_info = null;
  const raw = form.extras_info_json?.trim();
  if (raw) {
    try {
      extras_info = JSON.parse(raw);
    } catch {
      throw new Error('extras_info must be valid JSON or empty');
    }
  }
  const flow_value = form.flow_value === '' ? null : Number(form.flow_value);
  const std_value = form.std_value === '' ? null : Number(form.std_value);
  const exchange_rate = form.exchange_rate === '' ? null : Number(form.exchange_rate);
  if (flow_value == null || Number.isNaN(flow_value)) throw new Error('flow_value is required');
  if (std_value == null || Number.isNaN(std_value)) throw new Error('std_value is required');
  if (!form.date || !form.desc?.trim() || !form.categ_l0?.trim()) {
    throw new Error('date, description and categ_l0 are required');
  }
  return {
    date: form.date,
    hour: form.hour || '00:00:00',
    desc: form.desc.trim(),
    categ_l0: form.categ_l0.trim(),
    categ_l1: form.categ_l1?.trim() || null,
    categ_l2: form.categ_l2?.trim() || null,
    reference: form.reference?.trim() || null,
    flow_type: form.flow_type,
    flow_value,
    flow_currency: form.flow_currency || 'BRL',
    std_value,
    flow_account: form.flow_account?.trim() || null,
    extras_description: form.extras_description?.trim() || null,
    extras_category: form.extras_category?.trim() || null,
    extras_id: form.extras_id?.trim() || null,
    extras_cardnumber: form.extras_cardnumber?.trim() || null,
    extras_info,
    exchange_id: form.exchange_id?.trim() || null,
    exchange_rate: exchange_rate != null && !Number.isNaN(exchange_rate) ? exchange_rate : null,
    status: form.status?.trim() ?? '',
    source: form.source?.trim() ?? '',
    external_id: form.external_id?.trim() || null,
  };
};

const fieldClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-600 focus:border-green-600';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

/** `record` null/undefined = create; object with `id` = edit (same pattern as Assets location modal). */
const ModalFlowPreliminaryTransaction = ({ isOpen, record, onClose, onSaved }) => {
  const isCreate = !record?.id;
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const applyRecord = useCallback(() => {
    if (!record?.id) {
      setForm(emptyForm());
      return;
    }
    setForm(rowToForm(record));
  }, [record]);

  useEffect(() => {
    if (!isOpen) return;
    applyRecord();
  }, [isOpen, record, applyRecord]);

  const handleClose = () => {
    if (!submitting && !deleting) {
      setShowDeleteConfirm(false);
      onClose?.();
    }
  };

  const handleDelete = async () => {
    if (!record?.id) return;
    setShowDeleteConfirm(false);
    try {
      setDeleting(true);
      await deleteFlowPreliminaryTransaction(record.id);
      toast.success('Preliminary row deleted.');
      await Promise.resolve(onSaved?.());
      onClose?.();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let payload;
    try {
      payload = formToPayload(form);
    } catch (err) {
      toast.error(err.message || 'Invalid form');
      return;
    }
    try {
      setSubmitting(true);
      if (isCreate) {
        await createFlowPreliminaryTransaction(payload);
        toast.success('Preliminary row created.');
      } else {
        await updateFlowPreliminaryTransaction(record.id, payload);
        toast.success('Preliminary row updated.');
      }
      await Promise.resolve(onSaved?.());
      onClose?.();
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data || err.message;
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 z-[60] bg-black bg-opacity-50 flex justify-center items-center p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl relative max-h-[90vh] flex flex-col">
        {(submitting || deleting) && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/90 rounded-2xl">
            <div className="bg-white rounded-xl shadow-lg p-4 border border-gray-200">
              <SpinnerLoad />
            </div>
          </div>
        )}

        <div className="flex items-start justify-between gap-2 px-4 pt-4 pb-2 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900 pr-2">
            {isCreate ? 'Add preliminary transaction' : `Edit preliminary #${record.id}`}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting || deleting}
            className="text-gray-500 hover:text-gray-700 shrink-0 p-1 rounded-lg hover:bg-gray-100"
            title="Close"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-4 py-3 space-y-4 text-sm">
            <p className="text-xs text-gray-500">
              Staging row — same fields as official Flow transactions. Merge into Flow will be added later.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Date *</label>
                <input
                  type="date"
                  className={fieldClass}
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  disabled={submitting || deleting}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Hour</label>
                <input
                  type="text"
                  className={fieldClass}
                  value={form.hour}
                  onChange={(e) => setForm((f) => ({ ...f, hour: e.target.value }))}
                  disabled={submitting || deleting}
                  placeholder="HH:MM:SS"
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={labelClass}>Description *</label>
                <input
                  className={fieldClass}
                  value={form.desc}
                  onChange={(e) => setForm((f) => ({ ...f, desc: e.target.value }))}
                  disabled={submitting || deleting}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Category L0 *</label>
                <input
                  className={fieldClass}
                  value={form.categ_l0}
                  onChange={(e) => setForm((f) => ({ ...f, categ_l0: e.target.value }))}
                  disabled={submitting || deleting}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Category L1</label>
                <input
                  className={fieldClass}
                  value={form.categ_l1}
                  onChange={(e) => setForm((f) => ({ ...f, categ_l1: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Category L2</label>
                <input
                  className={fieldClass}
                  value={form.categ_l2}
                  onChange={(e) => setForm((f) => ({ ...f, categ_l2: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Reference</label>
                <input
                  className={fieldClass}
                  value={form.reference}
                  onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Flow type *</label>
                <select
                  className={fieldClass}
                  value={form.flow_type}
                  onChange={(e) => setForm((f) => ({ ...f, flow_type: e.target.value }))}
                  disabled={submitting || deleting}
                >
                  <option value="In">In</option>
                  <option value="Out">Out</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Flow value *</label>
                <input
                  type="number"
                  step="0.01"
                  className={fieldClass}
                  value={form.flow_value}
                  onChange={(e) => setForm((f) => ({ ...f, flow_value: e.target.value }))}
                  disabled={submitting || deleting}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Currency</label>
                <input
                  className={fieldClass}
                  value={form.flow_currency}
                  onChange={(e) => setForm((f) => ({ ...f, flow_currency: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Std value *</label>
                <input
                  type="number"
                  step="0.01"
                  className={fieldClass}
                  value={form.std_value}
                  onChange={(e) => setForm((f) => ({ ...f, std_value: e.target.value }))}
                  disabled={submitting || deleting}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>Flow account</label>
                <input
                  className={fieldClass}
                  value={form.flow_account}
                  onChange={(e) => setForm((f) => ({ ...f, flow_account: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Status</label>
                <input
                  className={fieldClass}
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Source</label>
                <input
                  className={fieldClass}
                  value={form.source}
                  onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>External id</label>
                <input
                  className={fieldClass}
                  value={form.external_id}
                  onChange={(e) => setForm((f) => ({ ...f, external_id: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Extras id</label>
                <input
                  className={fieldClass}
                  value={form.extras_id}
                  onChange={(e) => setForm((f) => ({ ...f, extras_id: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Exchange id</label>
                <input
                  className={fieldClass}
                  value={form.exchange_id}
                  onChange={(e) => setForm((f) => ({ ...f, exchange_id: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Exchange rate</label>
                <input
                  type="number"
                  step="0.000001"
                  className={fieldClass}
                  value={form.exchange_rate}
                  onChange={(e) => setForm((f) => ({ ...f, exchange_rate: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Extras description</label>
                <input
                  className={fieldClass}
                  value={form.extras_description}
                  onChange={(e) => setForm((f) => ({ ...f, extras_description: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Extras category</label>
                <input
                  className={fieldClass}
                  value={form.extras_category}
                  onChange={(e) => setForm((f) => ({ ...f, extras_category: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div>
                <label className={labelClass}>Extras card number</label>
                <input
                  className={fieldClass}
                  value={form.extras_cardnumber}
                  onChange={(e) => setForm((f) => ({ ...f, extras_cardnumber: e.target.value }))}
                  disabled={submitting || deleting}
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-3">
                <label className={labelClass}>Extras info (JSON)</label>
                <textarea
                  className={`${fieldClass} font-mono text-xs min-h-[4rem]`}
                  value={form.extras_info_json}
                  onChange={(e) => setForm((f) => ({ ...f, extras_info_json: e.target.value }))}
                  disabled={submitting || deleting}
                  placeholder="{}"
                />
              </div>
            </div>
          </div>

          <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap items-center gap-2 shrink-0 bg-gray-50 rounded-b-2xl">
            {!isCreate && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={submitting || deleting}
                className="shrink-0 rounded-xl border border-red-200 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Delete preliminary row
              </button>
            )}
            <div className="ml-auto flex flex-wrap justify-end gap-2 min-w-0">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting || deleting}
                className="px-4 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || deleting}
                className="px-5 py-2 rounded-xl bg-green-700 hover:bg-green-800 text-white text-sm font-medium disabled:opacity-50"
              >
                {isCreate ? 'Add' : 'Save'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>

    <ModalConfirm
      isOpen={showDeleteConfirm}
      onClose={() => !deleting && setShowDeleteConfirm(false)}
      onConfirm={handleDelete}
      overlayClassName="z-[70]"
      title="Delete preliminary row"
      message="This row will be removed permanently. This cannot be undone."
      confirmText={deleting ? 'Deleting…' : 'Delete'}
      cancelText="Cancel"
      disabled={deleting}
      confirmButtonClass={`px-4 py-2 rounded-xl ${deleting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} text-white shadow`}
      cancelButtonClass={`px-4 py-2 rounded-xl border border-gray-300 ${deleting ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
    />
    </>
  );
};

export default ModalFlowPreliminaryTransaction;
